/**
 * Devuelve la ventana a su sitio cuando iOS la deja corrida.
 *
 * EL PROBLEMA QUE RESUELVE
 * ------------------------
 * El marco de la aplicacion es de altura fija (`h-app overflow-hidden`), asi
 * que la VENTANA no deberia desplazarse nunca: lo que se mueve es `main`, por
 * dentro. Pero el teclado de iOS no respeta eso. Cuando el cajero toca el campo
 * de busqueda, iOS desplaza la ventana entera para dejar el campo a la vista
 * —aunque la raiz sea `overflow: hidden`, que Safari ignora en este caso— y al
 * cerrarse el teclado NO siempre la devuelve.
 *
 * El sintoma es el que se ve en el telefono: la barra inferior queda subida y
 * "pegada" ahi, con una franja muerta debajo, hasta que se recarga.
 *
 * POR QUE ES SEGURO FORZARLO
 * --------------------------
 * En este marco un desplazamiento de la ventana **nunca** es legitimo: no hay
 * nada que ver fuera de `h-app`. Asi que devolverla a cero no puede tapar
 * contenido ni pelearse con un gesto del usuario; solo deshace lo que hizo el
 * navegador por su cuenta.
 *
 * Se engancha a `focusout` (el momento en que se va el teclado), a los eventos
 * del viewport visual (que en iOS son los unicos que avisan del teclado) y al
 * giro del aparato, que reproduce el mismo defecto.
 */
import { useEffect } from 'react';

export function useViewportGuard(): void {
  useEffect(() => {
    const devolver = (): void => {
      if (window.scrollX !== 0 || window.scrollY !== 0) window.scrollTo(0, 0);
    };

    // Tras `focusout` el teclado todavia se esta yendo: iOS ajusta el
    // desplazamiento en los cuadros siguientes, asi que corregir en el mismo
    // instante no sirve de nada. Se vuelve a mirar cuando ya termino.
    const devolverPronto = (): void => {
      devolver();
      window.setTimeout(devolver, 100);
      window.setTimeout(devolver, 350);
    };

    const vv = window.visualViewport;
    window.addEventListener('focusout', devolverPronto);
    window.addEventListener('orientationchange', devolverPronto);
    vv?.addEventListener('resize', devolver);
    vv?.addEventListener('scroll', devolver);

    return () => {
      window.removeEventListener('focusout', devolverPronto);
      window.removeEventListener('orientationchange', devolverPronto);
      vv?.removeEventListener('resize', devolver);
      vv?.removeEventListener('scroll', devolver);
    };
  }, []);
}
